import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { useAuth } from '../AuthContext'
import { useSelectedHospital } from '../SelectedHospitalContext'
import { AvailabilityBar } from '../components/AvailabilityBar'
import { BedCountCell } from '../components/BedCountCell'
import { BedDiff } from '../components/BedDiff'
import { SaveToast } from '../components/SaveToast'
import { ApiError } from '../api/client'
import { patchBeds, type BedPatchBody } from '../api/bedInventory'
import {
  fetchMyHospital,
  hospitalMeKey,
  type BedState,
  type HospitalMe,
} from '../api/hospitals'

/**
 * Beds (M23-T12), drawn on `1194:9888` — the frame Figma named `Leads`
 * (M23 defect D2), whose breadcrumb also reads "Leads".
 *
 * The most consequential page in the admin: what it writes is what patients and
 * Emergency Help read (its emergency status is shown labelled "as reported by
 * the hospital" since M36's switch-over). It is also the one page with an
 * optimistic-concurrency contract, and the frame draws none of it — a bare
 * `Update` button and nothing else. **The frame is the visual reference; the
 * concurrency behaviour is not up for renegotiation.**
 */

const BED_TYPES = [
  { key: 'icu', label: 'ICU' },
  { key: 'general', label: 'General' },
  { key: 'private', label: 'Private' },
  { key: 'executive', label: 'Executive' },
  { key: 'maternity', label: 'Maternity' },
] as const

/**
 * Three, not the frame's two. `emergency_status` is
 * `'open' | 'divert' | 'closed'` and a two-way control cannot set a
 * three-value field (M23 gap B2). They mean different things to the emergency
 * flow: on divert this hospital stops appearing as a destination; closed is
 * closed.
 */
const STATUSES = ['open', 'divert', 'closed'] as const

const STATUS_COPY: Record<(typeof STATUSES)[number], string> = {
  open: 'Accepting',
  divert: 'On divert',
  closed: 'Closed',
}

const countField = z
  .number({ invalid_type_error: 'Enter a number' })
  .int('Whole numbers only')
  .min(0, 'Cannot be negative')

const bedSchema = z
  .object({
    icu_total: countField,
    icu_free: countField,
    general_total: countField,
    general_free: countField,
    private_total: countField,
    private_free: countField,
    executive_total: countField,
    executive_free: countField,
    maternity_total: countField,
    maternity_free: countField,
    emergency_status: z.enum(STATUSES),
    ambulance_available: z.boolean(),
  })
  .superRefine((v, ctx) => {
    for (const { key } of BED_TYPES) {
      const free = v[`${key}_free`]
      const total = v[`${key}_total`]
      if (free > total) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Free cannot exceed Total',
          path: [`${key}_free`],
        })
      }
    }
  })

export type BedFormValues = z.infer<typeof bedSchema>

const EMPTY: BedFormValues = {
  icu_total: 0,
  icu_free: 0,
  general_total: 0,
  general_free: 0,
  private_total: 0,
  private_free: 0,
  executive_total: 0,
  executive_free: 0,
  maternity_total: 0,
  maternity_free: 0,
  emergency_status: 'open',
  ambulance_available: false,
}

function toFormValues(b: BedState): BedFormValues {
  return {
    icu_total: b.icu_total,
    icu_free: b.icu_free,
    general_total: b.general_total,
    general_free: b.general_free,
    private_total: b.private_total,
    private_free: b.private_free,
    executive_total: b.executive_total,
    executive_free: b.executive_free,
    maternity_total: b.maternity_total,
    maternity_free: b.maternity_free,
    emergency_status: b.emergency_status,
    ambulance_available: b.ambulance_available,
  }
}

function nowTime(): string {
  return new Date().toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** The fields the admin actually changed, for the conflict diff. */
function dirtyValues(
  values: BedFormValues,
  dirty: Partial<Record<keyof BedFormValues, unknown>>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const key of Object.keys(dirty) as (keyof BedFormValues)[]) {
    if (dirty[key]) out[key] = values[key]
  }
  return out
}

export function EditBedsPage() {
  const { token, logout, user } = useAuth()
  const { hospitalId } = useSelectedHospital()
  const queryClient = useQueryClient()

  const isAmtc = user?.role === 'amtc_admin'
  const effectiveId = isAmtc ? hospitalId : null
  const meKey = hospitalMeKey(effectiveId)

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: meKey,
    queryFn: () => fetchMyHospital(token, effectiveId),
    enabled: Boolean(token) && (!isAmtc || Boolean(hospitalId)),
    refetchOnWindowFocus: false, // don't clobber in-progress edits
  })

  const [toastTime, setToastTime] = useState<string | null>(null)
  const [conflict, setConflict] = useState<{
    mine: Record<string, string | number | boolean>
  } | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    getValues,
    formState: { errors, isDirty, isValid, dirtyFields },
  } = useForm<BedFormValues>({
    resolver: zodResolver(bedSchema),
    mode: 'onChange',
    defaultValues: EMPTY,
  })

  // useWatch rather than watch(): watch() returns a fresh function each render,
  // which the React Compiler cannot memoize (it skips the whole component).
  const values = useWatch({ control, defaultValue: EMPTY })

  // Sync the form to the latest server state on load, after save, or after a
  // conflict refresh. `if_updated_at` is read straight from this query data.
  useEffect(() => {
    if (data) reset(toFormValues(data.beds))
  }, [data, reset])

  // Auto-dismiss the success toast.
  useEffect(() => {
    if (!toastTime) return
    const t = setTimeout(() => setToastTime(null), 3000)
    return () => clearTimeout(t)
  }, [toastTime])

  const mutation = useMutation({
    mutationFn: (body: BedPatchBody) =>
      patchBeds(token, data?.hospital.id ?? '', body),
    onSuccess: (newBeds) => {
      // Update the cache immediately so a quick second save uses the fresh
      // updated_at (no 409 race), then invalidate to re-sync from the server.
      queryClient.setQueryData(meKey, (old: HospitalMe | undefined) =>
        old ? { ...old, beds: newBeds } : old,
      )
      void queryClient.invalidateQueries({ queryKey: meKey })
      reset(toFormValues(newBeds))
      setSaveError(null)
      setConflict(null)
      setToastTime(nowTime())
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.code === 'concurrent_modification') {
          // Hold on to what the admin typed. Refreshing straight into the
          // server's numbers would silently discard it, and someone who has
          // just counted a ward should not have to count it again.
          setConflict({ mine: dirtyValues(getValues(), dirtyFields) })
          return
        }
        if (err.status === 401) {
          logout()
          return
        }
        if (err.status === 422 || err.code === 'invalid_bed_counts') {
          const field = err.details.field
          if (typeof field === 'string') {
            setError(field as keyof BedFormValues, { message: err.message })
            return
          }
        }
      }
      setSaveError('Could not save. Please try again.')
    },
  })

  const onSubmit = handleSubmit((v) => {
    setSaveError(null)
    const body: BedPatchBody = { if_updated_at: data?.beds.updated_at ?? '' }
    for (const key of Object.keys(dirtyFields) as (keyof BedFormValues)[]) {
      if (dirtyFields[key]) {
        ;(body as Record<string, unknown>)[key] = v[key]
      }
    }
    mutation.mutate(body)
  })

  if (isAmtc && !hospitalId) {
    return (
      // "above" was true until M24-T06: on the 72px rail the MANAGING block is
      // a bordered square and the selector is not there to point at. The
      // sentence has to be true in both sidebar states, and the page has no
      // business knowing which one it is in.
      // A HEADING, BECAUSE THIS BRANCH HAD NONE (M25-T02, finding F2).
      // The early return was a bare `<p>`, so this was the only one of the
      // twelve admin routes to render no level-one heading — and only in this
      // one state. A screen-reader user arriving here had nothing to orient on:
      // no page title, just a sentence telling them to use a control elsewhere.
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-2xl font-semibold text-ink-1">Beds</h1>
        <p className="text-sm text-ink-3">
          Pick a hospital from the{' '}
          <strong className="text-ink-1">Managing</strong> selector in the
          sidebar to edit its beds. Expand the sidebar if it is collapsed.
        </p>
      </div>
    )
  }

  if (isPending) {
    return (
      <div
        role="status"
        aria-label="Loading the bed editor"
        className="flex flex-col gap-3"
      >
        <div className="h-6 w-64 animate-pulse rounded bg-bg-sunken" />
        <div className="h-48 w-full max-w-3xl animate-pulse rounded bg-bg-sunken" />
      </div>
    )
  }

  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-[10px] border border-bed-bad/40 bg-bed-bad-bg px-4 py-3"
      >
        <p className="text-sm font-medium text-bed-bad">
          Could not load your hospital.
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-2 inline-flex h-8 items-center rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm font-medium"
        >
          Retry
        </button>
      </div>
    )
  }

  const saveDisabled = !isDirty || !isValid || mutation.isPending

  return (
    <section>
      <header className="mb-6">
        <h1 className="font-display text-2xl font-semibold text-ink-1">Beds</h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-3">
          What patients and the emergency flow see for {data.hospital.name}{' '}
          right now. Changes are live and recorded in the audit log.
        </p>
      </header>

      <form onSubmit={onSubmit}>
        <div className="overflow-hidden rounded-2xl border border-line-1">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Bed counts by ward</caption>
              <thead>
                <tr className="bg-bg-sunken text-left">
                  <th scope="col" className="px-5 py-3 font-medium text-ink-2">
                    Ward
                  </th>
                  <th scope="col" className="px-5 py-3 font-medium text-ink-2">
                    Free
                  </th>
                  <th scope="col" className="px-5 py-3 font-medium text-ink-2">
                    Total
                  </th>
                  <th
                    scope="col"
                    className="px-5 py-3 text-right font-medium text-ink-2"
                  >
                    Availability
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* All five. The frame draws three, and dropping Executive and
                    Maternity from the only screen that edits them is a silent
                    loss (M23 gap B1). */}
                {BED_TYPES.map(({ key, label }) => {
                  // useWatch types every field optional; the form always has
                  // a number here (defaultValues covers every field).
                  const free = values[`${key}_free`] ?? 0
                  const total = values[`${key}_total`] ?? 0
                  return (
                    <tr key={key} className="border-t border-line-1">
                      <th
                        scope="row"
                        className="px-5 py-4 text-left font-medium text-ink-1"
                      >
                        {label}
                      </th>
                      <td className="px-5 py-4">
                        <BedCountCell
                          id={`${key}-free`}
                          label="Free"
                          value={free}
                          max={total}
                          onStep={(n) =>
                            setValue(`${key}_free`, n, {
                              shouldDirty: true,
                              shouldValidate: true,
                            })
                          }
                          registration={register(`${key}_free`, {
                            valueAsNumber: true,
                          })}
                          error={errors[`${key}_free`]?.message}
                        />
                      </td>
                      <td className="px-5 py-4">
                        <BedCountCell
                          id={`${key}-total`}
                          label="Total"
                          value={total}
                          onStep={(n) =>
                            setValue(`${key}_total`, n, {
                              shouldDirty: true,
                              shouldValidate: true,
                            })
                          }
                          registration={register(`${key}_total`, {
                            valueAsNumber: true,
                          })}
                          error={errors[`${key}_total`]?.message}
                        />
                      </td>
                      <td className="px-5 py-4">
                        {/* Drawn from the same two values the steppers hold,
                            so the bar and the numbers cannot disagree the way
                            the frame's do (defect D9). */}
                        <AvailabilityBar free={free} total={total} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line-1 px-5 py-4">
            <p className="text-xs text-ink-3">
              Last updated {new Date(data.beds.updated_at).toLocaleString()}
            </p>
            {/* The frame labels this "Update". It stays "Save": the button is
                disabled until the form is dirty and valid, which is save
                semantics, and six shipped tests name it. A verb change with no
                product gain is not worth breaking them for. */}
            <button
              type="submit"
              disabled={saveDisabled}
              className="inline-flex h-10 items-center justify-center rounded-[8px] bg-accent px-5 text-sm font-semibold text-accent-on disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {saveError && (
          <p role="alert" className="mt-3 text-sm font-medium text-bed-bad">
            {saveError}
          </p>
        )}

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <fieldset className="rounded-2xl border border-line-1 p-6">
            <legend
              id="emergency-legend"
              className="px-1 font-display text-lg font-semibold text-ink-1"
            >
              Emergency status
            </legend>
            <p className="mb-4 text-sm text-ink-3">
              Emergency Help shows this to the public as reported by the
              hospital. It never hides the hospital: people can still find and
              call it.
            </p>
            <div
              role="radiogroup"
              aria-labelledby="emergency-legend"
              className="flex flex-wrap gap-3"
            >
              {STATUSES.map((s) => (
                <label
                  key={s}
                  className="flex items-center gap-2 rounded-[8px] border border-line-2 px-3 py-2 text-sm text-ink-1 has-[:checked]:border-accent has-[:checked]:bg-accent-bg has-[:checked]:font-semibold"
                >
                  <input
                    type="radio"
                    value={s}
                    {...register('emergency_status')}
                  />
                  {STATUS_COPY[s]}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="rounded-2xl border border-line-1 p-6">
            <legend className="px-1 font-display text-lg font-semibold text-ink-1">
              Ambulance
            </legend>
            <p className="mb-4 text-sm text-ink-3">
              Shown to callers choosing between hospital transport and their
              own.
            </p>
            <label className="flex w-fit items-center gap-2 rounded-[8px] border border-line-2 px-3 py-2 text-sm text-ink-1 has-[:checked]:border-accent has-[:checked]:bg-accent-bg has-[:checked]:font-semibold">
              <input type="checkbox" {...register('ambulance_available')} />
              Ambulance available
            </label>
          </fieldset>
        </div>
      </form>

      {toastTime && <SaveToast time={toastTime} />}

      {conflict && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="conflict-title"
          className="fixed inset-0 z-10 grid place-items-center bg-black/30 p-4"
        >
          <div className="w-full max-w-md rounded-[10px] border border-line-1 bg-bg-app p-6 shadow-pop">
            <h3
              id="conflict-title"
              className="text-base font-semibold text-ink-1"
            >
              Beds were updated elsewhere
            </h3>
            <p className="mt-2 text-sm text-ink-2">
              Someone else saved while you were editing, so your changes
              haven&apos;t been saved yet. Here is what you had typed:
            </p>
            <div className="mt-3 rounded-[8px] bg-bg-soft p-3 text-sm">
              <BedDiff
                before={toServerFields(data.beds, conflict.mine)}
                after={conflict.mine}
              />
            </div>
            <p className="mt-3 text-sm text-ink-3">
              Refreshing loads the latest counts. Your numbers stay in the form
              so you can check them against the new ones before saving again.
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setConflict(null)
                  void refetch()
                }}
                className="inline-flex h-9 items-center justify-center rounded-[8px] bg-accent px-3 text-sm font-medium text-accent-on"
              >
                Refresh and try again
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

/** The server's value for each field the admin changed, for the diff. */
function toServerFields(
  beds: BedState,
  mine: Record<string, string | number | boolean>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const key of Object.keys(mine)) {
    const v = (beds as unknown as Record<string, unknown>)[key]
    if (
      typeof v === 'string' ||
      typeof v === 'number' ||
      typeof v === 'boolean'
    ) {
      out[key] = v
    }
  }
  return out
}
